import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { Select, Button, Upload, message } from "antd";
import { UploadOutlined } from '@ant-design/icons';
import FlexBtn from "../../Components/Common/FlexBtn";
import { useTranslation } from "react-i18next";
import { getRequest, postRequest } from "../../services/index";

const AddBook = ({ onRequestClose, fetchBooks }) => {
  const [bookTitle, setBookTitle] = useState("");
  const [bookDescription, setBookDescription] = useState("");
  const [bookType, setBookType] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const userID = JSON.parse(localStorage.getItem("user"));
  const [documentFile, setDocumentFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const { t } = useTranslation();
  const [uploadLoading, setUploadLoading] = useState(false);

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const response = await getRequest(`/api/ClassAssignment/GetMyClasses?userId=${userID.userId}`);
      if (response.data.data) {
        const uniqueClasses = [...new Map(response.data.data.map(cls =>
          [cls.classId, { value: cls.classId, label: cls.className }]
        )).values()];

        const uniqueSubjects = [...new Map(response.data.data.map(cls =>
          [cls.subjectId, {
            value: cls.subjectId,
            label: cls.subjectName,
            classId: cls.classId
          }]
        )).values()];

        setClasses(uniqueClasses);
        setSubjects(uniqueSubjects);
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
      message.error("Failed to fetch classes");
    }
  };

  const handleClassChange = (value) => {
    setSelectedClass(value);
    setSelectedSubject(null);
  };

  const getFilteredSubjects = () => {
    return subjects.filter(subject => subject.classId === selectedClass);
  };

  const handleClose = () => {
    if (onRequestClose) {
      onRequestClose();
      fetchBooks();
    }
  };


  const handleSubmit = async () => {
    if (!selectedClass) {
      message.error("Please select a class");
      return;
    }
    if (!selectedSubject) {
      message.error("Please select a subject");
      return;
    }
    if (!bookTitle.trim()) {
      message.error("Please enter book title");
      return;
    }
    if (!bookDescription.trim()) {
      message.error("Please enter book description");
      return;
    }
    if (!bookType) {
      message.error("Please select book type");
      return;
    }
    if (!documentFile || documentFile.length === 0) {
      message.error("Please upload a file");
      return;
    }
    if (documentFile.some(file => file.size > 50 * 1024 * 1024)) {
      message.warning("File size should not be greater than 50 MB.");
      return;
    }

    setLoading(true);
    try {
      // First upload file to extract chapters
      const formData = new FormData();
      documentFile.forEach(file => {
        formData.append("files", file);
      });
      
      const extractResponse = await postRequest("/api/Document/documents-upload", formData, true);

      if (!extractResponse) {
        throw new Error("Failed to extract chapters");
      }
      
      // Then add book with the extracted URL
      const bookPayload = {
        title: bookTitle,
        description: bookDescription,
        classId: selectedClass,
        subjectId: selectedSubject,
        bookType: bookType,
        BookUrl: extractResponse.data[0] // Assuming the API returns the URL in this format
      };
      console.log("bookPayload",bookPayload)
      const response = await postRequest("/api/PaperGeneration/add-book", bookPayload);

      if (response.status === 200) {
        message.success("Book added successfully");
        handleClose();
      } else {
        message.error(response.message || "Failed to add book");
      }
    } catch (error) {
      console.error("Error submitting book:", error);
      message.error("Failed to add book");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid p-0 m-0 pb-4">
      <div className="row d-flex justify-contents-center p-0 m-0">
        <div className="col-md-12 examModalWrapper p-0 m-0">
          <div className="d-flex justify-content-between align-items-center px-4 col-md-12 examModalHeader">
            <h4 style={{ color: "#060317" }} className="fw-bold">{t("AddNewBook")} </h4>
            <div className="iconWrapper cursor-pointer" onClick={handleClose}><X /></div>
          </div>
        </div>
      </div>
      <div className="modalBody p-4">
        <div className="mb-4">
          <div className="row g-3">
            <div className="col-md-12">
              <label htmlFor="bookTitle" className="form-label">{t("BookName")}</label>
              <input
                type="text"
                className="form-control"
                id="bookTitle"
                value={bookTitle}
                onChange={(e) => setBookTitle(e.target.value)}
              />
            </div>

            <div className="col-md-12">
              <label htmlFor="bookDescription" className="form-label">{t("BookDescription")} </label>
              <textarea
                className="form-control"
                id="bookDescription"
                rows="3"
                value={bookDescription}
                onChange={(e) => setBookDescription(e.target.value)}
              ></textarea>
            </div>

            <div className="col-md-12">
              <label htmlFor="bookType" className="form-label">{t("BookType")}</label>
              <Select
                style={{ width: '100%' }}
                options={[
                  { value: 'cambridge', label: 'Cambridge Book' },
                  { value: 'others', label: 'Others Book' }
                ]}
                onChange={(value) => setBookType(value)}
                value={bookType}
                placeholder="Select book type"
              />
            </div>

            <div className="col-md-12">
              <label htmlFor="classSelect" className="form-label">{t("SelectClass")}</label>
              <Select
                style={{ width: '100%' }}
                options={classes}
                onChange={handleClassChange}
                value={selectedClass}
                loading={loading}
              />
            </div>

            <div className="col-md-12">
              <label htmlFor="subjectSelect" className="form-label">{t("SelectSubject")}</label>
              <Select
                style={{ width: '100%' }}
                options={getFilteredSubjects()}
                onChange={(value) => setSelectedSubject(value)}
                value={selectedSubject}
                loading={loading}
                disabled={!selectedClass}
              />
            </div>

            <div className="col-md-12">
              <Upload
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                beforeUpload={(file) => {
                  const allowedTypes = [
                    "application/pdf",
                    "image/jpeg",
                    "image/png",
                    "application/msword",
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  ];
                  if (!allowedTypes.includes(file.type)) {
                    alert(t("Invalid file type. Please select a PDF, image, or DOC file."));
                    return Upload.LIST_IGNORE;
                  }
                  setDocumentFile((prev) => [...(prev || []), file]);
                  return false;
                }}
                onRemove={(file) => {
                  setDocumentFile((prev) => prev.filter((f) => f.uid !== file.uid));
                }}
                listType="picture"
              >
                <Button type="primary" icon={<UploadOutlined />}>
                  {t("ContentUpload")}
                </Button>
              </Upload>

              {documentFile && (
                <div className="mt-2 text-success">
                  File uploaded successfully
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="col-md-12 px-4 mt-3 py-3">
        <div className="d-flex gap-3 justify-content-end">
          <div onClick={handleClose}>
            <FlexBtn label={t("Cancel")} color="#463C77" bgColor="#EDEBF1" />
          </div>
          <Button type="primary" loading={loading} onClick={handleSubmit}>
            {t("Create")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddBook;